import { Link } from 'react-router-dom';
import { cn } from '@localagent/ui/lib/utils';
import type { GoalActivityProject } from '@localagent/sdk';
import {
  BLOCKERS_UNAVAILABLE,
  PROJECT_LANE_COLORS,
  PROJECT_LANE_LABELS,
  VERIFICATION_LABELS,
  projectLane,
  projectTicketCounts,
  type BlockerList,
} from '../../lib/project-board-helpers';

const chipClasses = 'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium';

interface ProjectCardProps {
  project: GoalActivityProject;
  to: string;
  assigneeTitle: string;
  initiativeName?: string;
  blockers?: BlockerList;
  compact?: boolean;
}

export function ProjectCard({
  project,
  to,
  assigneeTitle,
  initiativeName,
  blockers,
  compact,
}: ProjectCardProps) {
  const lane = projectLane(project);
  const counts = projectTicketCounts(project);
  const blockersUnknown = blockers === BLOCKERS_UNAVAILABLE;
  const blockerList = blockersUnknown ? [] : blockers ?? [];
  const blocked = blockerList.length > 0;

  return (
    <div
      data-testid={`project-card-${project.id}`}
      data-lane={lane}
      data-blocked={blockersUnknown ? 'unknown' : blocked ? 'true' : 'false'}
      data-ticket-count={counts.total}
      data-compact={compact ? 'true' : 'false'}
      className={cn(
        'rounded-md bg-background shadow-sm transition-shadow hover:shadow-md',
        compact ? 'p-2' : 'p-3',
      )}
    >
      <Link
        to={to}
        data-testid={`project-card-link-${project.id}`}
        className="text-sm font-medium hover:underline"
      >
        {project.name}
      </Link>

      {initiativeName !== undefined && (
        <p className="mt-0.5 truncate text-[10px] text-muted-foreground" title={initiativeName}>
          {initiativeName}
        </p>
      )}

      <div className={cn('flex flex-wrap items-center gap-1.5', compact ? 'mt-1' : 'mt-2')}>
        <span className={cn(chipClasses, PROJECT_LANE_COLORS[lane])}>
          {PROJECT_LANE_LABELS[lane]}
        </span>
        <Link
          to={to}
          data-testid={`project-card-verification-${project.id}`}
          className={cn(chipClasses, 'bg-muted text-muted-foreground hover:bg-accent')}
        >
          {VERIFICATION_LABELS[project.verification_state]}
        </Link>
        {blocked && (
          <span
            data-testid={`project-card-blocked-${project.id}`}
            className={cn(chipClasses, 'bg-red-100 text-red-800')}
            title={`Blocked by ${blockerList.join(', ')}`}
          >
            Blocked by {blockerList.join(', ')}
          </span>
        )}
        {blockersUnknown && (
          <span
            data-testid={`project-card-blockers-unknown-${project.id}`}
            className={cn(chipClasses, 'bg-amber-100 text-amber-900')}
            title="The ordering read failed, so this card cannot say whether the project is blocked."
          >
            Blockers unavailable
          </span>
        )}
      </div>

      <div className={cn('flex items-center justify-between gap-2', compact ? 'mt-1' : 'mt-2')}>
        <span className="text-[10px] text-muted-foreground" data-testid={`project-card-assignee-${project.id}`}>
          {assigneeTitle}
        </span>
        <span
          className="text-[10px] text-muted-foreground"
          data-testid={`project-card-counts-${project.id}`}
          data-done={counts.done}
          data-total={counts.total}
        >
          {counts.done}/{counts.total} tickets
        </span>
      </div>

      {project.session_id && (
        <Link
          to={`/chat/${project.session_id}`}
          data-testid={`project-card-session-${project.id}`}
          data-session-id={project.session_id}
          title="Open the session working on this project"
          className={cn(
            chipClasses,
            'mt-2 gap-1 bg-primary/10 text-primary transition-colors hover:bg-primary/20'
          )}
        >
          Session
        </Link>
      )}
    </div>
  );
}
